/**
 * 휴가 · 출장 종류 — **한 곳에서 정한다** (2026-09-06 달력 판 · 2026-09-16 사본 합치기 · v2 V12 사전 정리).
 *
 * ★★ 서버는 **코드**(`annual`·`substitute` …)를 주고 화면이 말을 붙인다. 코드 목록은
 *    서버의 `LEAVE_TYPES` 여덟과 같다(`hr-helpers.ts`) — 회사가 이름을 바꿔 쓰는 길은
 *    「기준 설정」에 따로 있고, 그것을 안 쓰는 회사는 이 기본 이름을 본다.
 * ★ **모르는 코드는 그대로 보인다** — 회사가 만든 종류가 「??」로 보이면 그것부터 물어보게 된다.
 *
 * ## 2026-09-16 — 사본 넷이 하나가 됐다
 *
 * 같은 여덟 종류를 네 군데가 따로 적고 있었다. 하나를 고치면 나머지가 조용히 어긋난다:
 *   · 이 파일(`LEAVE_TYPE_LABEL`) · `HrWidgets.tsx`(**글자 그대로 같은 표**)
 *   · `api/hr.ts`(`LeaveType` union) · `AttendanceRequestModal.tsx`(`LEAVE_TYPES` — 몫까지)
 * 이제 **여기 하나**에서 나온다. 셋은 여기서 가져다 쓴다.
 *
 * ⚠ **`HRPage`(옛 `/hr`)는 합치지 않았다.** 거기는 ko/en/ja 세 언어 표를 따로 들고 있고
 *   한국어 낱말이 **다르다**(「오전반차」·「특별휴가」 vs 여기의 「반차(오전)」·「경조」).
 *   합치면 그 화면의 글자가 바뀐다 — 이 판의 조건은 「보이는 것이 한 글자도 안 바뀐다」이므로
 *   손대지 않는다. 낱말을 맞출지는 사람이 정할 일이다.
 *
 * ## 서버 카탈로그(DR2)가 오면
 *
 * `fromServerCatalog()` 하나만 갈아끼우면 된다 — 화면 쪽은 이미 이 파일만 본다.
 * ★ 항목 모양(`key`·`label`)은 **바꾸지 않는다.** 「기준 설정」이 회사 이름을 얹는
 *   `useCompanyCodes().apply()` 가 `T extends { key; label }` 제네릭이라, 칸 이름을
 *   `code` 로 바꾸면 그 기능이 조용히 빠진다.
 */

export type LeaveBucket = 'annual' | 'accrued' | 'reason';

export interface LeaveTypeEntry {
  key: string;
  label: string;
  /** 하루의 몫(반차 1/2 · 반반차 1/4). 날짜 범위로 쓰는 종류는 null. */
  fraction: number | null;
  /** 연차에서 빠지는가 · 따로 쌓이는가 · 사유가 있는가. 서버 `deductsAnnual` 과 같은 가름. */
  bucket: LeaveBucket;
}

/**
 * 휴가 종류 여덟 — **차례가 곧 화면 차례다**(고르개 · 묶음 안 줄 순서).
 *
 * ★ `fraction` 을 시간(240·120)으로 박지 않는다. 소정근로가 6시간인 사람의 반차는 3시간이어야
 *   하므로 길이는 언제나 `소정 × 몫` 으로 만든다. 소정은 서버 요약(`dailyMinutes`)이 알려 준다.
 * ★★ **대체휴가**는 2026-08-28 까지 신청 목록에만 없었다 — 서버는 받고 잔여도 따로 셌는데
 *    (`substituteTotal`) 신청 화면에만 빠져, 남은 것을 보면서 쓸 수는 없는 상태였다.
 *    몫은 없다(연차·병가처럼 날짜 범위로 쓴다 — 반차는 별도 종류다).
 * ★ 「대체휴무」가 아니라 **「대체휴가」** 다(2026-08-28) — 「휴무」는 공휴일 쪽 낱말이다.
 */
export const LEAVE_TYPE_CATALOG = [
  { key: 'annual', label: '연차', fraction: null, bucket: 'annual' },
  /*
   * S6 (2026-09-16) — 오전/오후를 **합친** 새 코드. 시작 시각을 따로 고르고 끝은 서버가 센다.
   * ★★★ 아래 옛 넷(`half_am` …)을 **지우지 않는다.** 서버가 여전히 다른 코드로 구분하고,
   *     지난 기록이 그것을 가리킨다 — 화면에서 「반차」 하나로 합치면 사람이 못 가린다
   *     (사용자 결정 2026-09-16: 「앞으로 고르는 유형」만 합친 이름을 쓴다).
   * ⚠ 서버 쪽 `codeOfLabel` 은 **이름을 먼저 첫 일치로** 되짚는다(`hr-request-materialize.ts:151`) —
   *   그래서 새 코드와 옛 코드가 **같은 이름을 쓰면 안 된다.** 여기 이름이 갈라져 있는 것이 그 방어다.
   */
  { key: 'half', label: '반차', fraction: 0.5, bucket: 'annual' },
  { key: 'quarter', label: '반반차', fraction: 0.25, bucket: 'annual' },
  { key: 'half_am', label: '반차(오전)', fraction: 0.5, bucket: 'annual' },
  { key: 'half_pm', label: '반차(오후)', fraction: 0.5, bucket: 'annual' },
  { key: 'quarter_am', label: '반반차(오전)', fraction: 0.25, bucket: 'annual' },
  { key: 'quarter_pm', label: '반반차(오후)', fraction: 0.25, bucket: 'annual' },
  { key: 'sick', label: '병가', fraction: null, bucket: 'reason' },
  { key: 'special', label: '경조', fraction: null, bucket: 'reason' },
  { key: 'substitute', label: '보상휴가', fraction: null, bucket: 'accrued' },
] as const satisfies readonly LeaveTypeEntry[];

/** 서버가 아는 휴가 코드 — 목록에서 뽑는다(따로 적지 않는다). */
export type LeaveTypeKey = (typeof LEAVE_TYPE_CATALOG)[number]['key'];

/**
 * 신청 화면이 쓰는 목록 — 키 · 이름 · 몫.
 * ★ 「기준 설정」의 `apply()` 가 이 모양(`key`·`label`)을 요구한다(위 머리말 참조).
 */
export const LEAVE_TYPES: ReadonlyArray<{ key: LeaveTypeKey; label: string; fraction: number | null }> =
  LEAVE_TYPE_CATALOG.map((t) => ({ key: t.key, label: t.label, fraction: t.fraction }));

/** 코드 → 이름. 목록에서 만든다. */
export const LEAVE_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  LEAVE_TYPE_CATALOG.map((t) => [t.key, t.label]),
);

export const TRIP_TYPE_LABEL: Record<string, string> = {
  business_trip: '출장',
  outside_work: '외근',
};

/**
 * 묶음 가름 — **서버 `deductsAnnual` 과 같아야 한다**
 * (`apps/chat-server/src/modules/hr/leave-type-attrs.ts:47-71`).
 * ★ 서버처럼 **모르는 코드는 연차에서 빠지는 것**으로 본다.
 */
export function leaveBucket(key: string): LeaveBucket {
  return LEAVE_TYPE_CATALOG.find((t) => t.key === key)?.bucket ?? 'annual';
}

/** 하루 안에서 끝나는 종류인가(반차 · 반반차) — 종료일 칸을 잠그는 판정. */
export function isPartialDayType(key: string): boolean {
  return LEAVE_TYPE_CATALOG.find((t) => t.key === key)?.fraction != null;
}

export function leaveTypeLabel(type: string | null | undefined): string | null {
  if (!type) return null;
  return LEAVE_TYPE_LABEL[type] ?? type;
}

export function tripTypeLabel(type: string | null | undefined): string | null {
  if (!type) return null;
  return TRIP_TYPE_LABEL[type] ?? type;
}

/**
 * 서버 카탈로그(DR2 `GET /hr/leave-types`)를 이 파일의 모양으로 옮긴다 — **아직 안 쓴다.**
 *
 * ★ 서버가 오기 전에 «얼굴»만 맞춰 둔다. DR2 가 배포되면 부르는 쪽에서 이 함수를 통과시킨
 *   목록을 `LEAVE_TYPE_CATALOG` 대신 넘기면 되고, 화면 코드는 건드리지 않는다.
 * ★ 서버가 `group` 을 안 주면 코드로 되짚는다 — 모르는 코드는 연차 쪽(서버와 같은 규칙).
 */
export function fromServerCatalog(
  rows: ReadonlyArray<{
    code: string; label: string; group?: string | null;
    fraction?: number | null; active?: boolean; sortOrder?: number | null;
  }>,
): LeaveTypeEntry[] {
  const bucketOf = (g: string | null | undefined, code: string): LeaveBucket =>
    g === 'accrued' || g === 'reason' || g === 'annual' ? g : leaveBucket(code);
  return rows
    .filter((r) => r.active !== false)
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((r) => ({
      key: r.code,
      label: r.label,
      fraction: r.fraction ?? null,
      bucket: bucketOf(r.group, r.code),
    }));
}
